import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import type { PracticeWorkingHour } from '@/types/settings-api';

/**
 * Update an existing working-hours profile (PROD-758).
 *   PUT /api/Practice/WorkingHour/{workingHourId}
 *
 * Note the SINGULAR `WorkingHour` in the URL path — distinct from
 * `WorkingHoursList` (the GET). Flutter source:
 * `updatePracticeWorkingHours` in `settings_data_source_impl.dart:1137`.
 *
 * Body shape mirrors the cURL — same as create but WITH `workingHourId`
 * and `practiceName` preserved. Per-slot fields match the create wire:
 *   - `id` is the slot's existing PK (preserved for updates)
 *   - `startHour` / `endHour` are HUMAN-FORMAT `"04 AM"` / `"11 PM"`
 *   - `startMinute` / `endMinute` carry the `" min"` suffix
 *
 * Top-level key is `practiceId` on the wire (not `practiceSetupId`).
 * Same React-to-wire field-rename as `useAddWorkingHours`.
 */
export function useUpdateWorkingHours(practiceSetupId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profile: PracticeWorkingHour) => {
      if (!profile.workingHourId) {
        throw new Error(
          'useUpdateWorkingHours requires a non-null workingHourId',
        );
      }
      const { practiceSetupId: _psid, ...rest } = profile;
      void _psid;
      const body = {
        ...rest,
        practiceId: practiceSetupId,
      };
      return put<unknown>(
        `/api/Practice/WorkingHour/${profile.workingHourId}`,
        body,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'working-hours-list', practiceSetupId],
      });
    },
  });
}
